import { useEffect, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Etichetta, Riquadro } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Avviso in calce ai messaggi in uscita (498) e cifratura verso il server di posta.
 *
 * Stanno in un file a parte da Posta.tsx perche' sono pezzi che la pagina monta in punti diversi:
 * il riquadro di modifica sotto la casella dell'ente, la nota nel compositore della webmail,
 * l'avviso sulle caselle salvate con la sicurezza «nessuna».
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const MASSIMO = 2000

const giorno = (v: string | null) => (v ? new Date(v).toLocaleDateString('it-IT') : '—')

/**
 * Il testo dell'avviso, sotto la casella dell'ente: lo vede chi ha `impostazione.modifica`, che e'
 * anche chi vede quella linguetta. Il server lo aggiunge in fondo alle email scritte dalle persone,
 * solo con la webmail e archiviazione integrale attiva; vuoto significa nessun avviso.
 *
 * Dal 31/10/2026 (499) il testo non si salva da qui: si chiede di cambiarlo, con una motivazione, e
 * vale solo dopo l'approvazione del Referente privacy (o del DPO) in «Approvazioni». Fino ad allora
 * resta in vigore quello di prima. Una richiesta alla volta.
 */
export function RiquadroAvvisoInCalce() {
  const [stato, setStato] = useState<posta.StatoAvviso | null>(null)
  const [richiesta, setRichiesta] = useState<posta.RichiestaAvviso | null>(null)
  const [testo, setTesto] = useState('')
  const [motivazione, setMotivazione] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    Promise.all([posta.leggiAvviso(), posta.leggiRichiestaAvviso()])
      .then(([s, r]) => { if (vivo) { setStato(s); setRichiesta(r); setTesto(r?.stato === 'in_attesa' ? r.testo : s.testo) } })
      .catch((e) => { if (vivo) setErrore(testoErrore(e)) })
    return () => { vivo = false }
  }, [])

  async function chiedi() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      await posta.chiediModificaAvviso(testo, motivazione.trim())
      setRichiesta(await posta.leggiRichiestaAvviso())
      setMotivazione('')
      setMessaggio('Richiesta inviata: il testo nuovo vale dopo l’approvazione del Referente privacy, nella pagina «Approvazioni».')
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  if (!stato) return errore ? <Avviso tipo="errore">{errore}</Avviso> : null
  const inAttesa = richiesta?.stato === 'in_attesa'

  return (
    <Riquadro
      titolo="Avviso in calce ai messaggi"
      azioni={
        <Etichetta tono={stato.in_vigore ? 'positivo' : 'neutro'}>
          {stato.in_vigore ? 'in vigore' : 'non in vigore'}
        </Etichetta>
      }
    >
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <Avviso tipo="informazione">
        Il gestionale aggiunge questo testo in fondo alle email scritte dalle persone che partono da
        una casella di CIDIS — webmail, risposte, email dalla scheda — quando la funzione «Webmail e
        archiviazione integrale della posta» è attiva. Non lo aggiunge alle email automatiche (inviti,
        codici di accesso, reimpostazione della password, avvisi, prove della casella). Chi scrive non
        lo vede e non lo può togliere.
      </Avviso>

      <div className="piccolo tenue">Testo in vigore</div>
      {stato.testo
        ? <p style={{ whiteSpace: 'pre-line' }}>{stato.testo}</p>
        : <p className="tenue">Nessun avviso: i messaggi escono senza.</p>}

      {richiesta && (
        <Avviso tipo={richiesta.stato === 'respinta' ? 'attenzione' : richiesta.stato === 'approvata' ? 'successo' : 'informazione'}>
          {richiesta.stato === 'in_attesa' && <>
            Testo nuovo in attesa del Referente privacy: chiesto da {richiesta.richiedente} il{' '}
            {giorno(richiesta.richiesta_il)}, scade il {giorno(richiesta.scade_il)}. Fino alla decisione
            resta in vigore il testo di sopra.
          </>}
          {richiesta.stato === 'approvata' && <>
            Testo approvato{richiesta.decisore ? ` da ${richiesta.decisore}` : ''} il {giorno(richiesta.decisa_il)}.
          </>}
          {richiesta.stato === 'respinta' && <>
            Testo respinto{richiesta.decisore ? ` da ${richiesta.decisore}` : ''} il {giorno(richiesta.decisa_il)}
            {richiesta.nota_decisione ? `: «${richiesta.nota_decisione}»` : '.'}
          </>}
        </Avviso>
      )}

      {posta.daCompilare(testo) && (
        <Avviso tipo="attenzione">
          Il testo contiene ancora campi «[DA COMPILARE: …]»: vanno scritti, a partire
          dall’indirizzo della pagina web con l’informativa estesa, prima di attivare la webmail.
        </Avviso>
      )}
      <AreaTesto etichetta={inAttesa ? 'Testo proposto' : 'Testo nuovo'} righe={10} valore={testo} cambia={setTesto}
                 maxLength={MASSIMO} disabilitato={inAttesa}
                 aiuto={`Italiano e inglese, separati da una riga vuota. ${testo.length} di ${MASSIMO} caratteri. `
                   + 'Vuoto: chiedi di togliere l’avviso.'} />
      {!inAttesa && (
        <>
          <AreaTesto etichetta="Motivazione" obbligatorio righe={3} valore={motivazione} cambia={setMotivazione}
                     maxLength={1000} aiuto="Almeno 10 caratteri. La legge chi decide." />
          <div className="riga">
            <Bottone variante="primario" inCorso={inCorso}
                     disabled={testo.trim() === stato.testo || motivazione.trim().length < 10}
                     onClick={() => void chiedi()}>
              Chiedi l’approvazione
            </Bottone>
          </div>
          <p className="piccolo tenue">
            Il testo nuovo vale solo dopo l’approvazione del Referente privacy (o del DPO), nella pagina
            «Approvazioni». Fino ad allora resta in vigore quello di sopra.
          </p>
        </>
      )}
    </Riquadro>
  )
}

/**
 * Nota nel compositore della webmail: in fondo al messaggio, che e' scritto da una persona, il server
 * aggiunge l'avviso. Senza
 * avviso in vigore non dice niente; se la lettura non riesce tace, perche' l'invio non ne dipende.
 */
export function NotaAvvisoInCalce() {
  const [stato, setStato] = useState<posta.StatoAvviso | null>(null)
  useEffect(() => {
    let vivo = true
    posta.leggiAvviso().then((s) => { if (vivo) setStato(s) }).catch(() => undefined)
    return () => { vivo = false }
  }, [])
  if (!stato?.in_vigore) return null
  return (
    <details className="piccolo tenue">
      <summary>In fondo al messaggio il gestionale aggiunge l’avviso sull’archiviazione della posta.</summary>
      <p style={{ whiteSpace: 'pre-line' }}>{stato.testo}</p>
    </details>
  )
}

// --- cifratura verso il server di posta ---------------------------------------------------------

/**
 * Una casella salvata con «nessuna» mostra il campo da scegliere, con il motivo: il valore resta
 * quello salvato finche' non se ne sceglie un altro, e salvarlo cosi' lo rifiuta l'API.
 */
export function sceltaSicurezza(valore: string | null | undefined, predefinito: string) {
  const nessuna = valore === 'nessuna'
  return {
    valore: nessuna ? '' : (valore || predefinito),
    vuota: nessuna ? 'da scegliere' : undefined,
    errore: nessuna
      ? '«Nessuna» non è più ammessa: password e messaggi viaggerebbero in chiaro. Scegli TLS o STARTTLS e controlla la porta.'
      : undefined,
  }
}

/** Avviso in testa alla configurazione di una casella salvata con «nessuna». */
export function AvvisoCifratura({ smtp, imap }: { smtp: string | null; imap: string | null }) {
  const invio = smtp === 'nessuna'
  const lettura = imap === 'nessuna'
  if (!invio && !lettura) return null
  const sospeso = invio && lettura ? 'l’invio e la lettura sono sospesi'
    : invio ? 'l’invio è sospeso' : 'la lettura è sospesa'
  return (
    <Avviso tipo="errore">
      Questa casella usa la sicurezza «nessuna» verso il server di posta: {sospeso} finché non
      scegli TLS o STARTTLS qui sotto e salvi.
    </Avviso>
  )
}

/** Nella webmail: la casella scelta ha un canale senza cifratura. */
export function AvvisoCifraturaWebmail({ casella }: { casella: posta.CasellaWebmail }) {
  const canali = casella.cifratura_assente ?? []
  if (!canali.length) return null
  const invio = canali.includes('invio')
  const lettura = canali.includes('lettura')
  const cosa = invio && lettura ? 'L’invio e la lettura sono sospesi' : invio ? 'L’invio è sospeso' : 'La lettura è sospesa'
  const dove = casella.ambito === 'gruppo' ? '«Caselle di gruppo»'
    : casella.ambito === 'organizzazione' ? '«Casella dell’ente»' : '«Casella personale»'
  return (
    <Avviso tipo="errore">
      {cosa} per {casella.indirizzo}: la casella usa la sicurezza «nessuna» verso il server di posta,
      e password e messaggi viaggerebbero in chiaro. Va scelta TLS o STARTTLS nella linguetta {dove}.
    </Avviso>
  )
}
